/**
 * Governance Report Panel
 * 
 * date_of_change: 2026-05-03
 * rule_reference: D&D 5e Character Data Governance Standards
 * 
 * Displays validation results with severity indicators and collapsible details.
 * Shows auto-correction summary with confidence levels.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CONFIDENCE } from '../../utils/CharacterGovernanceValidator';

const STATUS_ICONS = {
  success: '✓',
  warning: '⚠',
  error: '✗',
  info: 'ℹ'
};

const STATUS_COLORS = {
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6'
};

const CONFIDENCE_LABELS = {
  [CONFIDENCE.HIGH]: 'Fixed for you',
  [CONFIDENCE.MEDIUM]: 'Check this',
  [CONFIDENCE.LOW]: 'Needs your choice'
};

const CONFIDENCE_COLORS = {
  [CONFIDENCE.HIGH]: '#22c55e',
  [CONFIDENCE.MEDIUM]: '#f59e0b',
  [CONFIDENCE.LOW]: '#ef4444'
};

const getPlayerCorrectionReason = (reason = '') => {
  if (/sum of class levels/i.test(reason)) return 'Matched the character level to the listed class levels.';
  if (/numeric-keyed object/i.test(reason)) return 'Organized the equipment so it fits the character sheet.';
  if (/prose entries from equipment/i.test(reason)) return 'Moved descriptive notes out of the equipment list.';
  if (/disqualified action entries/i.test(reason)) return 'Removed entries that cannot be used as combat actions.';
  if (/sentence-like action names/i.test(reason)) return 'Shortened long action names so they are easier to use in combat.';
  if (/string class levels to integers/i.test(reason)) return 'Converted the listed class levels into usable numbers.';
  if (/normalized subclass/i.test(reason)) return 'Placed the subclass name in the correct spot on the sheet.';
  return 'Adjusted this detail so it fits the character sheet.';
};

const formatPlayerValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (value?.name || value?.label || value?.value) return String(value.name || value.label || value.value);
  return 'Character details';
};

/**
 * Governance Report Panel Component
 */
const GovernanceReportPanel = ({ report, onToggle, className = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    errors: true,
    warnings: false,
    info: false,
    corrections: false
  });
  
  if (!report || !report.summary) {
    return null;
  }
  
  const { summary, errors = [], warnings = [], info = [], corrections = [] } = report;
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const getStatusBadge = () => {
    const { errorCount = 0, warningCount = 0 } = summary;
    const status = summary.status || (errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : 'success');
    
    if (status === 'error') {
      return (
        <span className="governance-badge governance-badge-error">
          {STATUS_ICONS.error} {errorCount === 1 ? '1 item needs attention' : `${errorCount} items need attention`}
        </span>
      );
    }
    
    if (status === 'warning') {
      return (
        <span className="governance-badge governance-badge-warning">
          {STATUS_ICONS.warning} {warningCount === 1 ? '1 item worth checking' : `${warningCount} items worth checking`}
        </span>
      );
    }
    
    return (
      <span className="governance-badge governance-badge-success">
        {STATUS_ICONS.success} Checks complete
      </span>
    );
  };
  
  const getQuickStats = () => {
    const stats = [];
    if (summary.errorCount > 0) {
      stats.push(summary.errorCount === 1 ? '1 item needs attention' : `${summary.errorCount} items need attention`);
    }
    if (summary.warningCount > 0) {
      stats.push(summary.warningCount === 1 ? '1 item worth checking' : `${summary.warningCount} items worth checking`);
    }
    if (summary.correctionsApplied > 0) {
      stats.push(`${summary.correctionsApplied} fixed for you`);
    }
    if (stats.length === 0) {
      return 'All checks passed';
    }
    return stats.join(' • ');
  };
  
  return (
    <div className={`governance-report-panel ${className}`}>
      {/* Header Bar */}
      <button
        type="button"
        className="governance-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="governance-header-left">
          <span className="governance-header-icon">
            {STATUS_ICONS[summary.status || (summary.errorCount > 0 ? 'error' : summary.warningCount > 0 ? 'warning' : 'success')]}
          </span>
          <span className="governance-header-title">Paste Check</span>
          {getStatusBadge()}
        </div>
        <div className="governance-header-right">
          <span className="governance-quick-stats">{getQuickStats()}</span>
          <span className="governance-expand-icon">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </button>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="governance-content">
          {/* Summary Stats */}
          <div className="governance-stats-grid">
            <div className="governance-stat-item">
              <span className="governance-stat-value" style={{ color: STATUS_COLORS.error }}>
                {summary.errorCount}
              </span>
              <span className="governance-stat-label">Need Attention</span>
            </div>
            <div className="governance-stat-item">
              <span className="governance-stat-value" style={{ color: STATUS_COLORS.warning }}>
                {summary.warningCount}
              </span>
              <span className="governance-stat-label">Worth Checking</span>
            </div>
            <div className="governance-stat-item">
              <span className="governance-stat-value" style={{ color: STATUS_COLORS.info }}>
                {summary.infoCount}
              </span>
              <span className="governance-stat-label">Notes</span>
            </div>
            <div className="governance-stat-item">
              <span className="governance-stat-value" style={{ color: STATUS_COLORS.success }}>
                {summary.correctionsApplied}
              </span>
              <span className="governance-stat-label">Fixed for You</span>
            </div>
          </div>
          
          {/* Errors Section */}
          {errors.length > 0 && (
            <div className="governance-section">
              <div 
                className="governance-section-header governance-section-error"
                onClick={() => toggleSection('errors')}
              >
                <span className="governance-section-icon">{STATUS_ICONS.error}</span>
                <span className="governance-section-title">
                  Needs Your Attention ({errors.length})
                </span>
                <span className="governance-section-toggle">
                  {expandedSections.errors ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.errors && (
                <div className="governance-section-content">
                  {errors.map((error, idx) => (
                    <div key={idx} className="governance-issue governance-issue-error">
                      <span className="governance-issue-message">{error.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Warnings Section */}
          {warnings.length > 0 && (
            <div className="governance-section">
              <div 
                className="governance-section-header governance-section-warning"
                onClick={() => toggleSection('warnings')}
              >
                <span className="governance-section-icon">{STATUS_ICONS.warning}</span>
                <span className="governance-section-title">
                  Worth Checking ({warnings.length})
                </span>
                <span className="governance-section-toggle">
                  {expandedSections.warnings ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.warnings && (
                <div className="governance-section-content">
                  {warnings.map((warning, idx) => (
                    <div key={idx} className="governance-issue governance-issue-warning">
                      <span className="governance-issue-message">{warning.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Info Section */}
          {info.length > 0 && (
            <div className="governance-section">
              <div 
                className="governance-section-header governance-section-info"
                onClick={() => toggleSection('info')}
              >
                <span className="governance-section-icon">{STATUS_ICONS.info}</span>
                <span className="governance-section-title">
                  What SmartPaste Found ({info.length})
                </span>
                <span className="governance-section-toggle">
                  {expandedSections.info ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.info && (
                <div className="governance-section-content">
                  {info.map((item, idx) => (
                    <div key={idx} className="governance-issue governance-issue-info">
                      <span className="governance-issue-message">{item.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Auto-Corrections Section */}
          {corrections && corrections.length > 0 && (
            <div className="governance-section">
              <div 
                className="governance-section-header governance-section-corrections"
                onClick={() => toggleSection('corrections')}
              >
                <span className="governance-section-icon">🛠</span>
                <span className="governance-section-title">
                  Fixed for You ({corrections.length})
                </span>
                <span className="governance-section-toggle">
                  {expandedSections.corrections ? '▼' : '▶'}
                </span>
              </div>
              {expandedSections.corrections && (
                <div className="governance-section-content">
                  {corrections.map((correction, idx) => (
                    <div key={idx} className="governance-correction">
                      <div className="governance-correction-header">
                        <span 
                          className="governance-correction-confidence"
                          style={{ backgroundColor: CONFIDENCE_COLORS[correction.confidence] }}
                        >
                          {CONFIDENCE_LABELS[correction.confidence]}
                        </span>
                      </div>
                      <div className="governance-correction-reason">{getPlayerCorrectionReason(correction.reason)}</div>
                      {correction.confidence !== CONFIDENCE.HIGH && (
                        <div className="governance-correction-details">
                          <div className="governance-correction-original">
                            <span className="governance-correction-label">Pasted:</span>
                            <span>{formatPlayerValue(correction.original)}</span>
                          </div>
                          <div className="governance-correction-corrected">
                            <span className="governance-correction-label">Using:</span>
                            <span>{formatPlayerValue(correction.corrected)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GovernanceReportPanel.propTypes = {
  report: PropTypes.shape({
    summary: PropTypes.shape({
      passed: PropTypes.bool.isRequired,
      status: PropTypes.oneOf(['success', 'warning', 'error']),
      errorCount: PropTypes.number.isRequired,
      warningCount: PropTypes.number.isRequired,
      infoCount: PropTypes.number.isRequired,
      correctionsApplied: PropTypes.number.isRequired,
      retries: PropTypes.number.isRequired
    }).isRequired,
    errors: PropTypes.arrayOf(PropTypes.shape({
      ruleId: PropTypes.string.isRequired,
      severity: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      path: PropTypes.string
    })),
    warnings: PropTypes.arrayOf(PropTypes.shape({
      ruleId: PropTypes.string.isRequired,
      severity: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      path: PropTypes.string
    })),
    info: PropTypes.arrayOf(PropTypes.shape({
      ruleId: PropTypes.string.isRequired,
      severity: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired
    })),
    corrections: PropTypes.arrayOf(PropTypes.shape({
      path: PropTypes.string.isRequired,
      original: PropTypes.any,
      corrected: PropTypes.any,
      confidence: PropTypes.oneOf(['high', 'medium', 'low']).isRequired,
      reason: PropTypes.string
    }))
  }),
  onToggle: PropTypes.func,
  className: PropTypes.string
};

export default GovernanceReportPanel;
